import React, { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import {
  Tabs,
  Spin,
  message,
  Modal,
  Row,
  Col,
  Card,
  Progress,
  Alert,
  Checkbox, // 추가
  Tag, // 추가
} from 'antd'
import {
  FireOutlined,
  CheckCircleOutlined,
  ShoppingCartOutlined,
  SafetyOutlined,
  EditOutlined,
  CloseOutlined,
  LockOutlined,
  ThunderboltOutlined,
  DollarOutlined,
  InboxOutlined,
  WarningOutlined,
  TagOutlined,
  PercentageOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
  BankOutlined, // 추가
  CrownOutlined, // 추가
} from '@ant-design/icons'
import Page from '../components/atoms/page'
import SummerBlackFridayBulkApplicationModal from '../components/molecules/SummerBlackFridayBulkApplicationModal'
import Pagination from '../components/molecules/pagination'
import ListLimitSelector from '../components/atoms/listLimitSelector'
import ItemSearchComponent from '../components/ItemComponents/ItemSearchComponent'
import backendApis from '../utils/backendApis'
import resize from '../utils/resize'
import commaNumber from 'comma-number'
import SummerBlackFridayBulkCancellationModal from '../components/molecules/SummerBlackFridayBulkCancellationModal'
import bithumbLogo from '../images/summerBlackFriday/bithumb_logo_RGB_orange_가로형.png'
import bithumbLogo2 from '../images/summerBlackFriday/bithumb_logo_RGB_orange_세로형.png'
import alwayzLogo from '../images/logo.png'
import alwayzLogoLeft from '../images/LogoRedLetter.png'
import PremiumBrandApplicationModal from '../components/molecules/PremiumBrandApplicationModal'
import PremiumBrandStatusModal from '../components/molecules/PremiumBrandStatusModal'

const { TabPane } = Tabs

const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const SummerBlackFridayScreen = observer(({ sellerInfo }) => {
  const dealType = 'summerBlackFriday25'
  const [activeTab, setActiveTab] = useState('candidate')
  const [selectedItem, setSelectedItem] = useState(null)
  const [openApplicationModal, setOpenApplicationModal] = useState(false)
  const [openBulkApplicationModal, setOpenBulkApplicationModal] = useState(true)
  const [itemsInfo, setItemsInfo] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [itemsCount, setItemsCount] = useState(0)
  const [itemCondition, setItemCondition] = useState({})
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [openBulkCancellationModal, setOpenBulkCancellationModal] =
    useState(false)
  const [showPremiumApplicationModal, setShowPremiumApplicationModal] =
    useState(true)
  const [premiumRegistrationStatus, setPremiumRegistrationStatus] =
    useState(null)
  const [showPremiumStatusModal, setShowPremiumStatusModal] = useState(false)
  const [premiumRegistrationData, setPremiumRegistrationData] = useState(null)

  // 프로모션 정보 - 빗썸 콜라보 정보 추가
  const promotionInfo = {
    title: '올웨이즈 X 빗썸 여름 블랙프라이데이',
    subtitle: 'BITHUMB SUMMER COLLABORATION',
    period: '2025.07.24 ~ 2025.08.14',
    minDiscountRate: 5,
    defaultDiscountRate: 10,
    recommendedDiscountRate: 20,
    commissionRate: 15,
    benefits: [
      '카카오톡 알림 발송',
      '프로모션 이벤트 페이지 노출',
      '특별 배지 부여',
      '특별 띠 배너 노출',
    ],
    bithumbBenefits: {
      logo: bithumbLogo,
      color: '#FF6C00', // 빗썸 주황색으로 수정
    },
  }

  // 실시간 재고 계산 함수
  const calculateTotalStock = (optionsInfo) => {
    if (!optionsInfo?.optionPrices) return 0

    let totalStock = 0
    const flattenOptions = (options) => {
      options.forEach((option) => {
        if (Array.isArray(option)) {
          flattenOptions(option)
        } else if (option && !option.sold_out && option.stockNumber) {
          totalStock += option.stockNumber
        }
      })
    }

    flattenOptions(optionsInfo.optionPrices)
    return totalStock
  }

  // 재고 상태 계산 함수
  const getStockStatus = (totalStock) => {
    if (totalStock === 0) {
      return { status: 'sold_out', color: 'red', text: '품절' }
    } else if (totalStock < 10) {
      return { status: 'hurry_up', color: 'orange', text: '재고 부족' }
    } else if (totalStock < 50) {
      return { status: 'low', color: 'gold', text: '재고 소진 중' }
    } else {
      return { status: 'normal', color: 'green', text: '재고 충분' }
    }
  }

  // 검색 핸들러
  const handleSearch = (selectedSearchField, itemQuery) => {
    fetchSegmentedItemsInfo(true, selectedSearchField, itemQuery)
  }

  // 상품 정보 가져오기
  const fetchSegmentedItemsInfo = async (
    isNewCondition,
    selectedSearchField,
    itemQuery,
  ) => {
    if (isLoading) return

    setIsLoading(true)
    const itemCondition = {}

    // 검색 조건 추가
    if (itemQuery) {
      if (selectedSearchField === 'itemTitle') {
        itemCondition.itemTitle = { $regex: escapeRegExp(itemQuery) }
      } else if (selectedSearchField === 'itemId') {
        itemCondition._id = { $in: [itemQuery] }
      }
    }

    // 탭별 조건 설정
    if (activeTab === 'candidate') {
      // 신청 가능한 상품: 등급 제한 없음, 품절만 제외
      itemCondition.soldOut = false
      itemCondition.outOfStock = { $ne: null }
      // 등급 제한 제거 - 모든 등급 신청 가능
      itemCondition.$or = [
        { itemFilters: { $eq: null } },
        {
          itemFilters: {
            $not: {
              $elemMatch: {
                $and: [{ key: 'deal', value: dealType }],
              },
            },
          },
        },
      ]
    } else {
      // 신청 완료된 상품
      itemCondition.soldOut = false
      itemCondition.outOfStock = { $ne: null }
      itemCondition.$or = [
        {
          itemFilters: {
            $elemMatch: {
              $and: [{ key: 'deal', value: dealType }],
            },
          },
        },
      ]
    }

    // 새로운 조건으로 검색 시 전체 개수 먼저 가져오기
    if (isNewCondition) {
      const itemsCountResult = await backendApis.getSellerItemsCountByCondition(
        {
          type: 'item',
          itemCondition,
        },
      )
      if (itemsCountResult?.status === 200) {
        setItemsCount(itemsCountResult?.data)
      } else {
        setItemsCount(0)
        setPage(1)
        setItemsInfo([])
        message.error('상품 목록을 불러오는 중 문제가 발생했습니다.')
        setIsLoading(false)
        return
      }
    }

    // 상품 정보 가져오기
    try {
      const result = await backendApis.getSellerSegementedItemsInfoByCondition(
        {
          type: 'item',
          itemCondition,
        },
        page,
        limit,
      )

      if (result?.status === 2000) {
        let itemsData = result?.data?.itemsInfo || []

        // 신청 완료 탭인 경우 딜 정보와 실시간 재고 정보 추가로 가져오기
        if (activeTab === 'complete' && itemsData.length > 0) {
          const itemIds = itemsData.map((item) => item._id)

          // 딜 정보 가져오기
          const dealOptionsResult = await backendApis.getDealOptionsInfoArray(
            itemIds,
            dealType,
          )

          // 실시간 상품 정보 가져오기 (현재 재고 확인용)
          const currentItemsResult = await backendApis.loadItemSpecificInfo(
            itemIds,
            { optionsInfo: 1, soldOut: 1, hurryUp: 1 }, // 필요한 필드만 프로젝션
          )

          if (
            dealOptionsResult?.status === 200 &&
            currentItemsResult?.status === 200
          ) {
            const dealOptionsData = dealOptionsResult?.data || []
            const currentItemsData = currentItemsResult?.data || []

            // 모든 정보를 병합
            itemsData = itemsData.map((item) => {
              const dealInfo = dealOptionsData.find(
                (deal) => deal.itemId?.toString() === item._id?.toString(),
              )
              const currentInfo = currentItemsData.find(
                (current) => current._id?.toString() === item._id?.toString(),
              )

              // 현재 실시간 재고 계산
              const currentTotalStock = calculateTotalStock(
                currentInfo?.optionsInfo,
              )
              const dealTotalStock = calculateTotalStock(
                dealInfo?.dealOptionsInfo,
              )

              return {
                ...item,
                dealTeamPurchasePrice: dealInfo?.dealTeamPurchasePrice,
                dealOptionsInfo: dealInfo?.dealOptionsInfo,
                dealDiscountRate: dealInfo?.dealOptionsInfo?.discountRate,
                // 실시간 재고 정보 추가
                currentOptionsInfo: currentInfo?.optionsInfo,
                currentTotalStock,
                dealTotalStock,
                stockStatus: getStockStatus(currentTotalStock),
                soldOut: currentInfo?.soldOut || false,
                hurryUp: currentInfo?.hurryUp || false,
              }
            })
          }
        }

        setItemsInfo(itemsData)
        setItemCondition(itemCondition)
        setIsDataLoaded(true)
      } else if (result?.status === 2001) {
        // 페이지 초과 시 마지막 페이지로 이동
        const maxPage = Math.ceil(result.data?.itemsCount / limit) || 1
        const retryResult =
          await backendApis.getSellerSegementedItemsInfoByCondition(
            {
              type: 'item',
              itemCondition,
            },
            maxPage,
            limit,
          )
        if (retryResult?.status === 2000) {
          setPage(maxPage)
          setItemsInfo(retryResult?.data?.itemsInfo)
          setItemCondition(itemCondition)
          setIsDataLoaded(true)
        } else {
          setPage(1)
          setItemsInfo([])
          setIsDataLoaded(false)
          if (retryResult?.status !== 2001) {
            message.error('상품 목록을 불러오는 중 문제가 발생했습니다.')
          }
        }
      }
    } catch (error) {
      message.error('데이터를 불러오는데 실패했습니다')
      setIsDataLoaded(false)
    } finally {
      setIsLoading(false)
    }
  }

  // 탭, 페이지, 리미트 변경 시 데이터 새로 가져오기
  useEffect(() => {
    fetchSegmentedItemsInfo(true)
  }, [activeTab, limit])

  useEffect(() => {
    // 페이지가 변경되면 항상 데이터를 가져와야 함
    fetchSegmentedItemsInfo(false)
  }, [page])

  // 화면 진입 시 이벤트 로그 및 모달 표시
  useEffect(() => {
    backendApis.recordEventLog('SummerBlackFridayScreen', 'entered screen')

    // 자동 모달 표시를 제거하거나 충분한 지연 시간을 둠
    const checkExistingApplications = async () => {
      // 데이터 로딩이 완료될 때까지 대기
      setTimeout(async () => {
        const itemCondition = {
          soldOut: false,
          outOfStock: { $ne: null },
          $or: [
            {
              itemFilters: {
                $elemMatch: {
                  $and: [{ key: 'deal', value: dealType }],
                },
              },
            },
          ],
        }

        const result = await backendApis.getSellerItemsCountByCondition({
          type: 'item',
          itemCondition,
        })

        // 이미 신청한 상품이 없는 경우에만 모달 표시
        if (!result?.data || result.data === 0) {
          // 하루에 한 번만 표시하도록 체크
          const today = new Date().toDateString()
          const hasShownToday = localStorage.getItem(`summerBF_modal_${today}`)

          if (!hasShownToday) {
            setOpenBulkApplicationModal(true)
            localStorage.setItem(`summerBF_modal_${today}`, 'true')
          }
        }
      }, 2000) // 2초 대기 후 실행
    }

    checkExistingApplications()
  }, [])

  // 프리미엄 브랜드관 상태 확인 (수정)
  useEffect(() => {
    const checkPremiumStatus = async () => {
      const result = await backendApis.getSummerBlackFridayPremiumBrandStatus()
      console.log(result)
      if (result?.status === 200 && result.data) {
        setPremiumRegistrationStatus(result.data.registrationStatus)
        setPremiumRegistrationData(result.data)
      }
    }
    checkPremiumStatus()
  }, [])

  // 프리미엄 브랜드관 상태 확인 함수를 컴포넌트 레벨에 정의
  const checkPremiumBrandStatus = async () => {
    try {
      const result = await backendApis.getSummerBlackFridayPremiumBrandStatus()
      console.log('Premium status check result:', result)
      if (result?.status === 200 && result.data) {
        setPremiumRegistrationStatus(result.data.registrationStatus)
        setPremiumRegistrationData(result.data)
      }
    } catch (error) {
      console.error('Error checking premium status:', error)
    }
  }

  // 프리미엄 브랜드관 상태 확인 (초기 로드)
  useEffect(() => {
    checkPremiumBrandStatus()
  }, [])

  // 전역 스타일 추가 (useEffect에서)
  useEffect(() => {
    // 애니메이션 키프레임 추가
    const style = document.createElement('style')
    style.textContent = `
      @keyframes shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
      .animate-shimmer {
        animation: shimmer 2s infinite;
      }
    `
    document.head.appendChild(style)

    return () => {
      document.head.removeChild(style)
    }
  }, [])

  // 개선된 카드 컴포넌트
  const SummerBlackFridayCard = ({
    itemInfo,
    onApply,
    onCancel,
    isApplied,
  }) => {
    // 가격 정보
    const displayPrice =
      isApplied && itemInfo?.dealTeamPurchasePrice
        ? itemInfo.dealTeamPurchasePrice
        : itemInfo?.teamPurchasePrice || 0
    const originalPrice = itemInfo?.teamPurchasePrice || 0
    const discountRate =
      isApplied && originalPrice > 0
        ? Math.round((1 - displayPrice / originalPrice) * 100)
        : promotionInfo.recommendedDiscountRate

    // 기타 정보
    const itemRank = itemInfo?.rankInfo?.itemRank || 'N/A'
    const lowestPrice = itemInfo?.crawledItemTheLowest?.price || 0
    const priceGap =
      lowestPrice > 0
        ? Math.round(((displayPrice - lowestPrice) / lowestPrice) * 100)
        : null

    // 재고 정보
    const currentStock = itemInfo?.currentTotalStock || 0
    const dealStock = itemInfo?.dealTotalStock || 0
    const stockStatus = itemInfo?.stockStatus || getStockStatus(currentStock)

    return (
      <div className='group h-full'>
        <Card
          className='h-full overflow-hidden border-0 shadow-sm hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1'
          bodyStyle={{
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {/* 상품 이미지 영역 - 고정 높이 */}
          <div className='relative h-48 overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100'>
            <img
              src={resize(itemInfo?.mainImageUris?.[0])}
              alt={itemInfo?.itemTitle}
              className='w-full h-full object-cover group-hover:scale-110 transition-transform duration-700'
            />

            {/* 상단 뱃지들 */}
            <div className='absolute top-2 left-2 right-2 flex justify-between items-start'>
              {/* 왼쪽: 할인율 */}
              {isApplied && discountRate > 0 && (
                <div className='bg-gradient-to-r from-red-500 to-pink-500 text-white px-2.5 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1'>
                  <PercentageOutlined className='text-[10px]' />
                  {discountRate}% OFF
                </div>
              )}

              {/* 오른쪽: 등급 */}
              <div className='ml-auto'>
                <div
                  className={`
                    rounded-full px-2.5 py-1 text-xs font-bold shadow-md backdrop-blur-sm
                    ${
                      itemRank === 'S'
                        ? 'bg-purple-500/90 text-white'
                        : itemRank === 'A'
                        ? 'bg-blue-500/90 text-white'
                        : itemRank === 'B'
                        ? 'bg-green-500/90 text-white'
                        : itemRank === 'C'
                        ? 'bg-yellow-500/90 text-white'
                        : 'bg-gray-500/90 text-white'
                    }
                  `}
                >
                  {itemRank}
                </div>
              </div>
            </div>

            {/* 하단 재고 상태 (신청 완료 시) */}
            {isApplied && stockStatus && (
              <div className='absolute bottom-2 left-2'>
                <div
                  className={`
                    px-2.5 py-1 rounded-full text-xs font-medium backdrop-blur-sm flex items-center gap-1
                    ${
                      stockStatus.status === 'sold_out'
                        ? 'bg-red-500/90 text-white'
                        : stockStatus.status === 'hurry_up'
                        ? 'bg-orange-500/90 text-white'
                        : stockStatus.status === 'low'
                        ? 'bg-yellow-500/90 text-white'
                        : 'bg-green-500/90 text-white'
                    }
                  `}
                >
                  <InboxOutlined className='text-[10px]' />
                  <span className='font-bold'>{commaNumber(currentStock)}</span>
                  개
                </div>
              </div>
            )}

            {/* 품절 오버레이 */}
            {itemInfo?.soldOut && (
              <div className='absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center'>
                <div className='bg-white/90 text-gray-900 px-6 py-3 rounded-xl font-bold shadow-2xl'>
                  품절
                </div>
              </div>
            )}
          </div>

          {/* 상품 정보 영역 - flex-grow로 남은 공간 채우기 */}
          <div className='flex-grow flex flex-col p-4'>
            {/* 상품명 - 2줄 고정 */}
            <h3 className='font-medium text-gray-900 text-sm line-clamp-2 mb-3 min-h-[40px]'>
              {itemInfo?.itemTitle}
            </h3>

            {/* 가격 정보 */}
            <div className='mb-3'>
              {isApplied ? (
                // 신청 완료: 할인 전후 가격
                <div className='space-y-1'>
                  <div className='flex items-center gap-2'>
                    <span className='text-xs text-gray-500 line-through'>
                      {commaNumber(originalPrice)}원
                    </span>
                    <span className='text-xs text-red-500 font-medium'>
                      {discountRate}%↓
                    </span>
                  </div>
                  <div className='flex items-baseline gap-1'>
                    <TagOutlined className='text-red-500 text-xs' />
                    <span className='text-xl font-bold text-gray-900'>
                      {commaNumber(displayPrice)}
                    </span>
                    <span className='text-xs text-gray-500'>원</span>
                  </div>
                </div>
              ) : (
                // 신청 가능: 현재가만
                <div className='flex items-baseline gap-1'>
                  <span className='text-xl font-bold text-gray-900'>
                    {commaNumber(displayPrice)}
                  </span>
                  <span className='text-xs text-gray-500'>원</span>
                </div>
              )}
            </div>

            {/* 온라인 최저가 대비 - 컴팩트하게 */}
            {priceGap !== null && (
              <div
                className={`
                  inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium mb-3
                  ${
                    priceGap <= 5
                      ? 'bg-green-50 text-green-700'
                      : 'bg-amber-50 text-amber-700'
                  }
                `}
              >
                <span className='opacity-70'>최저가 대비</span>
                <span className='font-bold'>
                  {priceGap > 0 ? '+' : ''}
                  {priceGap}%
                </span>
              </div>
            )}

            {/* 재고 진행률 (신청 완료 시) - 심플하게 */}
            {isApplied && dealStock > 0 && (
              <div className='mb-3'>
                <Progress
                  percent={Math.round((currentStock / dealStock) * 100)}
                  strokeColor={{
                    '0%':
                      stockStatus.status === 'sold_out'
                        ? '#ef4444'
                        : stockStatus.status === 'hurry_up'
                        ? '#f97316'
                        : stockStatus.status === 'low'
                        ? '#f59e0b'
                        : '#10b981',
                    '100%':
                      stockStatus.status === 'sold_out'
                        ? '#dc2626'
                        : stockStatus.status === 'hurry_up'
                        ? '#ea580c'
                        : stockStatus.status === 'low'
                        ? '#d97706'
                        : '#059669',
                  }}
                  size='small'
                  showInfo={false}
                  className='mb-1'
                />
                <div className='flex justify-between text-[10px] text-gray-500'>
                  <span>
                    재고 {Math.round((currentStock / dealStock) * 100)}%
                  </span>
                  <span>신청: {commaNumber(dealStock)}개</span>
                </div>
              </div>
            )}

            {/* spacer - 버튼을 아래로 밀기 */}
            <div className='flex-grow' />

            {/* 액션 버튼 - 하단 고정 */}
            {isApplied ? (
              <div className='grid grid-cols-2 gap-2'>
                <button
                  type='button'
                  onClick={() => onApply(itemInfo)}
                  className='px-3 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 text-xs font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-1.5'
                >
                  <EditOutlined className='text-sm' />
                  수정
                </button>
                <button
                  type='button'
                  onClick={() => onCancel(itemInfo)}
                  className='px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-1.5'
                >
                  <CloseOutlined className='text-sm' />
                  취소
                </button>
              </div>
            ) : (
              <button
                type='button'
                disabled={itemInfo?.soldOut}
                onClick={() => onApply(itemInfo)}
                className={`
                  relative w-full overflow-hidden rounded-lg font-medium py-3
                  transition-all duration-300 
                  ${
                    itemInfo?.soldOut
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-[#FF6C00] to-[#E55A00] text-white hover:from-[#E55A00] hover:to-[#CC4D00] transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl'
                  }
                `}
              >
                <span className='relative flex items-center justify-center gap-2 text-sm'>
                  {itemInfo?.soldOut ? (
                    <>
                      <LockOutlined />
                      품절
                    </>
                  ) : (
                    <>
                      <FireOutlined />
                      프로모션 신청
                    </>
                  )}
                </span>
              </button>
            )}
          </div>
        </Card>
      </div>
    )
  }

  // 전체 상품 일괄 취소 처리 함수
  const handleBulkCancel = () => {
    setOpenBulkCancellationModal(true)
  }

  return (
    <Page title='올웨이즈 X 빗썸 여름 블랙프라이데이' Icon={FireOutlined}>
      {/* 프로모션 배너 - 빗썸 주황색 스타일로 변경 */}
      <div className='relative bg-gradient-to-r from-[#FF6C00] to-[#E55A00] text-white rounded-lg p-8 mb-6 overflow-hidden'>
        {/* 배경 패턴 */}
        <div className='absolute inset-0 opacity-10'>
          <div className='absolute inset-0 bg-gradient-to-br from-orange-400 via-orange-600 to-red-600' />
        </div>

        <div className='relative flex justify-between items-start'>
          <div className='flex-1'>
            {/* 빗썸 로고와 타이틀 */}
            <div className='flex items-center gap-4 mb-4'>
              <img
                src={alwayzLogo}
                alt='Alwayz Logo'
                className='h-10 rounded'
              />
              <span className='text-xl font-bold text-white uppercase tracking-wider'>
                X
              </span>
              <img
                src={promotionInfo.bithumbBenefits.logo}
                alt='Bithumb Logo'
                className='h-10 bg-white rounded px-3 py-1'
              />
            </div>

            <h1 className='text-4xl font-black mb-2 flex items-center'>
              <FireOutlined className='mr-3 text-yellow-300 text-5xl animate-pulse' />
              <span className='bg-gradient-to-r from-white via-yellow-300 to-yellow-100 text-transparent bg-clip-text'>
                여름 블랙 프라이데이
              </span>
            </h1>

            <p className='text-xl opacity-90 mb-2 font-bold'>
              🌴 빗썸과 함께하는 특별한 여름 블랙프라이데이 프로모션!
            </p>
            <p className='text-sm opacity-75 mb-4'>
              대한민국 대표 종합 금융 플랫폼 빗썸과의 특별 콜라보레이션으로 더욱
              강력한 마케팅 효과를 경험하세요
            </p>

            <div className='grid grid-cols-2 md:grid-cols-5 gap-4 mt-6'>
              <div className='bg-white/10 backdrop-blur-sm rounded-lg p-4'>
                <p className='text-sm opacity-80'>기간</p>
                <p className='font-bold'>{promotionInfo.period}</p>
                <div className='mt-1.5 pt-1.5 text-[10px] text-gray-600'>
                  ※ 프로모션 일정은 운영 정책에 따라 조정될 수 있습니다.
                </div>
              </div>
              <div className='bg-white/10 backdrop-blur-sm rounded-lg p-4'>
                <p className='text-sm opacity-80'>최소 할인율</p>
                <p className='font-bold text-2xl'>
                  {promotionInfo.minDiscountRate}%
                </p>
              </div>
              <div className='bg-white/10 backdrop-blur-sm rounded-lg p-4'>
                <p className='text-sm opacity-80'>권장 할인율</p>
                <p className='font-bold text-2xl text-yellow-300'>
                  {promotionInfo.recommendedDiscountRate}%
                </p>
              </div>
            </div>
          </div>

          {/* 빗썸 로고 워터마크 */}
          <div className='absolute right-8 bottom-4 opacity-50 flex items-center gap-2'>
            <img
              src={bithumbLogo2}
              alt='Bithumb'
              className='w-[10vw] h-[10vw]'
            />
          </div>
        </div>
      </div>

      {/* 정책 안내 - 빗썸 혜택 카드 추가 */}
      <Row gutter={16} className='mb-8'>
        <Col xs={24} md={6}>
          <Card className='h-full border-0 shadow-sm'>
            <div className='flex items-start gap-3'>
              <div className='bg-green-100 rounded-lg p-2'>
                <CheckCircleOutlined className='text-green-600 text-xl' />
              </div>
              <div className='flex-1'>
                <h3 className='font-semibold text-gray-900 mb-2'>가격 정책</h3>
                <p className='text-gray-600 text-sm'>
                  온라인 최저가 대비{' '}
                  <span className='font-semibold text-green-600'>+5% 이내</span>{' '}
                  가격으로 판매 가능합니다. AI가 실시간으로 가격을
                  모니터링합니다.
                </p>
              </div>
            </div>
          </Card>
        </Col>

        {/* 빗썸 혜택 카드 - 주황색으로 수정 */}
        <Col xs={24} md={6}>
          <Card className='h-full border-2 border-[#FF6C00] shadow-lg bg-gradient-to-br from-orange-50 to-amber-50'>
            <div className='flex items-start gap-3'>
              <div className='rounded-lg p-2'>
                <img src={bithumbLogo2} alt='Bithumb' className='w-5 h-5' />
              </div>
              <div className='flex-1'>
                <h3 className='font-semibold text-gray-900 mb-2'>
                  빗썸 특별 혜택
                </h3>
                <p className='text-gray-700 text-sm font-medium'>
                  <span className='font-bold text-[#FF6C00]'>
                    빗썸 캐시백 보장 지급
                  </span>{' '}
                  및 특별 혜택으로 고객 구매 전환율 향상 및 참여 유도를 극대화
                  합니다.
                </p>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} md={6}>
          <Card className='h-full border-0 shadow-sm'>
            <div className='flex items-start gap-3'>
              <div className='bg-purple-100 rounded-lg p-2'>
                <DollarOutlined className='text-purple-600 text-xl' />
              </div>
              <div className='flex-1'>
                <h3 className='font-semibold text-gray-900 mb-2'>
                  수수료 안내
                </h3>
                <p className='text-gray-600 text-sm'>
                  프로모션 수수료{' '}
                  <span className='font-semibold text-purple-600'>15%</span>는{' '}
                  <span className='font-semibold'>쿠폰 지급 등 딜 활성화</span>
                  에 사용됩니다.
                </p>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} md={6}>
          <Card className='h-full border-0 shadow-sm'>
            <div className='flex items-start gap-3'>
              <div className='bg-blue-100 rounded-lg p-2'>
                <SafetyOutlined className='text-blue-600 text-xl' />
              </div>
              <div className='flex-1'>
                <h3 className='font-semibold text-gray-900 mb-2'>검수 안내</h3>
                <p className='text-gray-600 text-sm'>
                  신청 상품은{' '}
                  <span className='font-semibold text-blue-600'>
                    24시간 이내
                  </span>{' '}
                  전수 검사를 진행합니다. 검수 결과는 해당 페이지에서 확인하실
                  수 있습니다.
                </p>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* 플로팅 버튼들 - 상태에 따라 색상 변경 */}
      <div className='fixed bottom-6 right-6 z-50 flex flex-col gap-3'>
        {/* 프리미엄 브랜드관 버튼 */}
        <div className='group relative'>
          <button
            type='button'
            onClick={() => {
              if (
                premiumRegistrationStatus === 'approved' ||
                premiumRegistrationStatus === 'pending'
              ) {
                // 상태 모달 표시
                setShowPremiumStatusModal(true)
              } else {
                // 바로 신청 모달 표시 (가이드 모달 건너뛰기)
                setShowPremiumApplicationModal(true)
              }
            }}
            className={`
              ${
                premiumRegistrationStatus === 'approved'
                  ? 'bg-gradient-to-r from-green-600 to-green-700'
                  : premiumRegistrationStatus === 'pending'
                  ? 'bg-gradient-to-r from-yellow-600 to-yellow-700'
                  : 'bg-gradient-to-r from-gray-900 to-gray-700'
              } 
              text-white rounded-full p-4 shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 flex items-center gap-3 pr-6
            `}
          >
            <CrownOutlined className='text-2xl text-yellow-400' />
            <span className='font-medium'>
              {premiumRegistrationStatus === 'approved'
                ? '프리미엄 브랜드관 선정'
                : premiumRegistrationStatus === 'pending'
                ? '프리미엄 브랜드관 심사중'
                : '프리미엄 브랜드관'}
            </span>
          </button>
        </div>

        {/* FAQ 버튼 */}
        <div
          className='group relative'
          onClick={() =>
            window.open(
              'https://www.notion.so/FAQ-2290b952423c803fbb0edcb7ea229c38',
              '_blank',
            )
          }
        >
          <button
            type='button'
            className='bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-full p-4 shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 flex items-center gap-3 pr-6'
          >
            <QuestionCircleOutlined className='text-2xl' />
            <span className='font-medium'>FAQ 보기</span>
          </button>
          <div className='absolute -top-8 right-0 bg-gray-800 text-white text-xs px-3 py-1 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap'>
            궁금한 점이 있으신가요?
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className='flex items-center justify-between mb-6 pb-4 border-b'>
        <div className='flex gap-6'>
          <button
            type='button'
            onClick={() => {
              setActiveTab('candidate')
              setPage(1)
            }}
            className={`pb-2 font-medium transition-all ${
              activeTab === 'candidate'
                ? 'text-[#FF6C00] border-b-2 border-[#FF6C00]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            신청 가능 상품
            {activeTab === 'candidate' && itemsCount > 0 && (
              <span className='ml-2 bg-orange-100 text-[#FF6C00] px-2 py-0.5 rounded-full text-sm'>
                {itemsCount}
              </span>
            )}
          </button>
          <button
            type='button'
            onClick={() => {
              setActiveTab('complete')
              setPage(1)
            }}
            className={`pb-2 font-medium transition-all ${
              activeTab === 'complete'
                ? 'text-[#FF6C00] border-b-2 border-[#FF6C00]'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            신청 완료
            {activeTab === 'complete' && itemsCount > 0 && (
              <span className='ml-2 bg-orange-100 text-[#FF6C00] px-2 py-0.5 rounded-full text-sm'>
                {itemsCount}
              </span>
            )}
          </button>
        </div>

        <div className='flex items-center gap-3'>
          {/* 전체 상품 신청 버튼 - 빗썸 주황색 스타일 */}
          {activeTab === 'candidate' && itemsCount > 0 && (
            <button
              type='button'
              onClick={() => setOpenBulkApplicationModal(true)}
              className='px-4 py-2 bg-gradient-to-r from-[#FF6C00] to-[#E55A00] text-white font-medium rounded-lg hover:from-[#E55A00] hover:to-[#CC4D00] transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
            >
              <ThunderboltOutlined />
              전체 상품 일괄 신청
            </button>
          )}

          {/* 전체 상품 취소 버튼 - 신청 완료 탭 */}
          {activeTab === 'complete' && itemsCount > 0 && (
            <button
              type='button'
              onClick={handleBulkCancel}
              className='px-4 py-2 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-[1.02]'
            >
              <DeleteOutlined />
              전체 상품 일괄 취소
            </button>
          )}

          <ItemSearchComponent onSearch={handleSearch} />
          <ListLimitSelector limit={limit} setLimit={setLimit} />
        </div>
      </div>

      {/* 상품 그리드 - 반응형 개선 */}
      {isLoading ? (
        <div className='flex justify-center items-center h-64'>
          <Spin size='large' tip='상품을 불러오는 중...' />
        </div>
      ) : (
        <>
          <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-4'>
            {itemsInfo.map((itemInfo) => (
              <SummerBlackFridayCard
                key={`${itemInfo._id}`}
                itemInfo={itemInfo}
                dealType={dealType}
                isApplied={activeTab === 'complete'}
                onApply={(item) => {
                  setSelectedItem(item)
                  setOpenApplicationModal(true)
                }}
                onCancel={async (item) => {
                  Modal.confirm({
                    title: '신청 취소',
                    content: '프로모션 신청을 취소하시겠습니까?',
                    okText: '취소하기',
                    cancelText: '돌아가기',
                    onOk: async () => {
                      const response = await backendApis.deleteDealOptionsInfo(
                        item._id,
                        dealType,
                      )
                      if (response?.status === 200) {
                        message.success('신청이 취소되었습니다')
                        fetchSegmentedItemsInfo(true)
                      }
                    },
                  })
                }}
              />
            ))}
          </div>

          {/* 빈 상태 */}
          {itemsInfo.length === 0 && (
            <div className='flex flex-col items-center justify-center py-20'>
              <ShoppingCartOutlined className='text-6xl text-gray-300 mb-4' />
              <p className='text-lg font-medium text-gray-700 mb-2'>
                {activeTab === 'candidate'
                  ? '신청 가능한 상품이 없습니다'
                  : '신청한 상품이 없습니다'}
              </p>
              <p className='text-gray-500'>
                {activeTab === 'candidate'
                  ? '상품을 등록하고 프로모션에 참여해보세요'
                  : '프로모션 기간을 놓치지 마세요!'}
              </p>
            </div>
          )}

          {/* 페이지네이션 */}
          {itemsCount > limit && ( // limit보다 클 때만 표시
            <div className='mt-8 flex justify-center'>
              <Pagination
                total={itemsCount}
                limit={limit}
                page={page}
                setPage={setPage}
              />
            </div>
          )}

          {/* 디버깅 정보 (개발 중에만 사용) */}
          <div className='mt-4 text-center text-sm text-gray-500'>
            전체 상품: {itemsCount}개 | 페이지당 표시: {limit}개 | 현재 페이지:{' '}
            {page}
          </div>
        </>
      )}

      {/* 신청 모달 - 단일 상품도 벌크 모달 사용 */}
      {openApplicationModal && selectedItem && (
        <SummerBlackFridayBulkApplicationModal
          showModal={openApplicationModal}
          setShowModal={setOpenApplicationModal}
          dealType={dealType}
          promotionInfo={promotionInfo}
          singleItem={selectedItem} // 단일 상품 전달
          onSuccess={() => {
            fetchSegmentedItemsInfo(true)
            message.success('프로모션 신청이 완료되었습니다')
          }}
          onClose={() => {
            setOpenApplicationModal(false)
          }}
        />
      )}

      {/* 전체 신청 모달 */}
      {openBulkApplicationModal && (
        <SummerBlackFridayBulkApplicationModal
          showModal={openBulkApplicationModal}
          setShowModal={setOpenBulkApplicationModal}
          dealType={dealType}
          promotionInfo={promotionInfo}
          // singleItem prop 없음 - 벌크 모드
          onSuccess={() => {
            fetchSegmentedItemsInfo(true)
            message.success('전체 상품 프로모션 신청이 완료되었습니다')
          }}
          onClose={() => {
            fetchSegmentedItemsInfo(true)
          }}
        />
      )}

      {/* 전체 취소 모달 추가 */}
      {openBulkCancellationModal && (
        <SummerBlackFridayBulkCancellationModal
          showModal={openBulkCancellationModal}
          setShowModal={setOpenBulkCancellationModal}
          dealType={dealType}
          promotionInfo={promotionInfo}
          onSuccess={() => {
            fetchSegmentedItemsInfo(true)
            message.success('선택한 상품의 프로모션 신청이 취소되었습니다')
          }}
        />
      )}

      {/* 프리미엄 브랜드관 신청 모달만 유지 */}
      {showPremiumApplicationModal && (
        <PremiumBrandApplicationModal
          showModal={showPremiumApplicationModal}
          setShowModal={setShowPremiumApplicationModal}
          sellerInfo={sellerInfo}
          premiumRegistrationStatus={premiumRegistrationStatus}
          onSuccess={() => {
            checkPremiumBrandStatus()
            setShowPremiumApplicationModal(false)
          }}
          onClose={() => {
            setShowPremiumApplicationModal(false)
          }}
        />
      )}

      {/* 프리미엄 브랜드관 상태 모달 */}
      {showPremiumStatusModal && premiumRegistrationData && (
        <PremiumBrandStatusModal
          showModal={showPremiumStatusModal}
          setShowModal={setShowPremiumStatusModal}
          registrationData={premiumRegistrationData}
          onStatusChange={(newStatus) => {
            setPremiumRegistrationStatus(newStatus)
            if (newStatus === 'cancelled') {
              setPremiumRegistrationData(null)
            }
          }}
        />
      )}
    </Page>
  )
})

export default SummerBlackFridayScreen
